/**
 * KAN-912: удаление сообщений в ленте переписки (кабинет и /m).
 * handlerFor(message): обработчик "Удалить" для пузыря, если сообщение наше,
 * сервер разрешил удаление (can_delete) и пользователю открыт DELETE этого
 * сообщения (canUserCallApi по api_access менеджера); иначе undefined.
 * target: сообщение, открытое в DeleteMessageDialog; close закрывает диалог.
 */
import { useCallback, useContext, useState } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import type { CurrentUser } from '@/hooks/useCurrentUser';
import { canDeleteMessage } from '@/lib/messageDelete';
import type { EstimateMessage } from '@/types/estimate';

export interface MessageDeletion {
  handlerFor: (message: EstimateMessage) => ((message: EstimateMessage) => void) | undefined;
  target: EstimateMessage | null;
  close: () => void;
}

export function useMessageDeletion(clientId: string | null | undefined): MessageDeletion {
  // Пользователь из кеша ['currentUser'] без подписки (как useCanOpenPath): его
  // загружает ManagerRouteGuard до раскладки, а лента /m рисуется и вне
  // QueryClientProvider (изолированные тесты): без кеша действует роль по умолчанию.
  const queryClient = useContext(QueryClientContext);
  const user = queryClient?.getQueryData<CurrentUser>(['currentUser']);
  const [target, setTarget] = useState<EstimateMessage | null>(null);
  const close = useCallback(() => setTarget(null), []);
  const handlerFor = useCallback(
    (message: EstimateMessage) => (canDeleteMessage(user, clientId, message) ? setTarget : undefined),
    [user, clientId]
  );
  return { handlerFor, target: clientId ? target : null, close };
}
